import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import PropertyDetailPage from './PropertyDetailPage';
import MapComponent from '../components/MapComponent';
import { apiUrl, normalizeMediaUrl, storageUrl } from '../utils/apiBase';
import { normalizeLotUnitForAdmin } from '../utils/formatters';

const normalizeBooleanSelect = (value) => {
  if (value === true || value === 1 || value === '1' || value === 'true') return 'yes';
  if (value === false || value === 0 || value === '0' || value === 'false') return 'no';
  if (value === 'yes' || value === 'no') return value;
  return value ?? '';
};

const buildInitialFormData = (property, draft = {}) => {
  const safeDraft = draft || {};
  const lotSizeValue = safeDraft.lot_size_value ?? property?.lot_size_value ?? property?.size ?? '';
  const lotSizeUnit = normalizeLotUnitForAdmin(
    safeDraft.lot_size_unit ?? property?.lot_size_unit ?? property?.size_unit
  );
  const saleTypeDetail = safeDraft.sale_type_detail
    ?? property?.sale_type_detail
    ?? (property?.property_type === 'rent' ? 'for_rent' : 'for_sale');

  return {
    client_reference: safeDraft.client_reference ?? property?.client_reference ?? '',
    title: safeDraft.title ?? property?.title ?? '',
    description: safeDraft.description ?? property?.description ?? '',
    property_category: safeDraft.property_category ?? property?.property_category ?? 'land_lot',
    sale_type_detail: saleTypeDetail,
    property_type: safeDraft.property_type ?? property?.property_type ?? (saleTypeDetail === 'for_rent' ? 'rent' : 'sale'),
    is_owner: normalizeBooleanSelect(safeDraft.is_owner ?? property?.is_owner ?? ''),
    has_title: normalizeBooleanSelect(safeDraft.has_title ?? property?.has_title ?? ''),
    contact_name: safeDraft.contact_name ?? property?.contact_name ?? '',
    phone: safeDraft.phone ?? property?.phone ?? '',
    whatsapp: safeDraft.whatsapp ?? property?.whatsapp ?? '',
    email: safeDraft.email ?? property?.email ?? '',
    address: safeDraft.address ?? property?.address ?? '',
    city: safeDraft.city ?? property?.city ?? '',
    region: safeDraft.region ?? property?.region ?? '',
    country: safeDraft.country ?? property?.country ?? '',
    lot_size_value: lotSizeValue,
    lot_size_unit: lotSizeUnit,
    price: safeDraft.price ?? property?.price ?? '',
    currency: safeDraft.currency ?? property?.currency ?? 'USD',
    living_area_value: safeDraft.living_area_value ?? property?.living_area_value ?? '',
    living_area_unit: safeDraft.living_area_unit ?? property?.living_area_unit ?? 'sqm',
    bedrooms: safeDraft.bedrooms ?? property?.bedrooms ?? '',
    bathrooms: safeDraft.bathrooms ?? property?.bathrooms ?? '',
    year_built: safeDraft.year_built ?? property?.year_built ?? '',
    latitude: safeDraft.latitude ?? property?.latitude ?? '',
    longitude: safeDraft.longitude ?? property?.longitude ?? '',
    road_access: normalizeBooleanSelect(safeDraft.road_access ?? property?.road_access ?? ''),
    road_surface: safeDraft.road_surface ?? property?.road_surface ?? '',
    in_community: normalizeBooleanSelect(safeDraft.in_community ?? property?.in_community ?? ''),
    water_connection: normalizeBooleanSelect(safeDraft.water_connection ?? property?.water_connection ?? ''),
    electricity: normalizeBooleanSelect(safeDraft.electricity ?? property?.electricity ?? ''),
    location_source: safeDraft.location_source ?? property?.location_source ?? 'gps',
    mark_sold: safeDraft.status === 'sold',
  };
};

const PropertyVerificationPage = () => {
  const { token } = useParams();
  const { t, i18n } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(true);
  const [property, setProperty] = useState(null);
  const [previewProperty, setPreviewProperty] = useState(null);
  const [step, setStep] = useState('edit');
  const [formData, setFormData] = useState(() => buildInitialFormData(null, {}));
  const [keepImageIds, setKeepImageIds] = useState([]);
  const [draftImages, setDraftImages] = useState([]);
  const [newImages, setNewImages] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiUrl(`/property-verifications/${token}`));
        const data = await response.json();
        if (!response.ok || !data.success) {
          setError(data.message || 'Invalid link');
          setLoading(false);
          return;
        }
        const propertyData = data.data?.property;
        const draftPayload = data.data?.draft || null;
        setProperty(propertyData);
        setPreviewProperty(data.data?.preview || null);
        setFormData(buildInitialFormData(propertyData, draftPayload || {}));
        const existingIds = (propertyData?.images || []).map((img) => img.id).filter(Boolean);
        setKeepImageIds(draftPayload?.keep_image_ids || existingIds);
        setDraftImages(draftPayload?.new_images || []);
      } catch (err) {
        setError(t('verification.errors.loadFailed'));
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [token, t]);

  const totalImageCount = useMemo(() => {
    return keepImageIds.length + draftImages.length + newImages.length;
  }, [keepImageIds, draftImages.length, newImages.length]);

  const handleExistingImageToggle = (id) => {
    setKeepImageIds((prev) => (
      prev.includes(id) ? prev.filter((value) => value !== id) : [...prev, id]
    ));
  };

  const handleDraftImageRemove = (path) => {
    setDraftImages((prev) => prev.filter((img) => img.file_path !== path));
  };

  const handleNewImagesChange = (event) => {
    const files = Array.from(event.target.files || []);
    if (!files.length) return;
    const jpgFiles = files.filter((file) => /\.(jpe?g)$/i.test(file.name));
    if (jpgFiles.length !== files.length) {
      toast.error(t('verification.images.jpgOnly'));
    }
    setNewImages((prev) => [...prev, ...jpgFiles]);
    event.target.value = '';
  };

  const handleRemoveNewImage = (index) => {
    setNewImages((prev) => prev.filter((_, idx) => idx !== index));
  };

  const buildPayload = () => {
    return {
      client_reference: formData.client_reference,
      title: formData.title,
      description: formData.description,
      property_category: formData.property_category,
      sale_type_detail: formData.sale_type_detail,
      property_type: formData.property_type || (formData.sale_type_detail === 'for_rent' ? 'rent' : 'sale'),
      is_owner: normalizeBooleanSelect(formData.is_owner),
      has_title: normalizeBooleanSelect(formData.has_title),
      contact_name: formData.contact_name,
      phone: formData.phone,
      whatsapp: formData.whatsapp,
      email: formData.email,
      address: formData.address,
      city: formData.city,
      region: formData.region,
      country: formData.country,
      lot_size_value: formData.lot_size_value,
      lot_size_unit: formData.lot_size_unit,
      size: formData.lot_size_value,
      size_unit: formData.lot_size_unit,
      price: formData.price,
      currency: formData.currency,
      living_area_value: formData.living_area_value,
      living_area_unit: formData.living_area_unit,
      bedrooms: formData.bedrooms,
      bathrooms: formData.bathrooms,
      year_built: formData.year_built,
      latitude: formData.latitude,
      longitude: formData.longitude,
      road_access: normalizeBooleanSelect(formData.road_access),
      road_surface: formData.road_surface,
      in_community: normalizeBooleanSelect(formData.in_community),
      water_connection: normalizeBooleanSelect(formData.water_connection),
      electricity: normalizeBooleanSelect(formData.electricity),
      location_source: formData.location_source,
      status: formData.mark_sold ? 'sold' : '',
    };
  };

  const handlePreview = async () => {
    if (totalImageCount > 20) {
      toast.error(t('verification.images.maxLimit'));
      return;
    }

    setSubmitting(true);
    try {
      const payload = buildPayload();
      const form = new FormData();
      Object.entries(payload).forEach(([key, value]) => {
        if (value !== undefined) {
          form.append(key, value === null ? '' : String(value));
        }
      });
      form.append('keep_image_ids', JSON.stringify(keepImageIds));
      form.append('keep_new_images', JSON.stringify(draftImages.map((img) => img.file_path)));
      newImages.forEach((file) => form.append('images[]', file));

      const response = await fetch(apiUrl(`/property-verifications/${token}/preview`), {
        method: 'POST',
        body: form,
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.message || t('verification.errors.previewFailed'));
        return;
      }

      setPreviewProperty(data.data?.preview || null);
      setStep('preview');
      toast.success(t('verification.previewReady'));
    } catch (err) {
      toast.error(t('verification.errors.previewFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    setSubmitting(true);
    try {
      const response = await fetch(apiUrl(`/property-verifications/${token}/confirm`), {
        method: 'POST',
      });
      const data = await response.json();
      if (!response.ok || !data.success) {
        toast.error(data.message || t('verification.errors.confirmFailed'));
        return;
      }
      setStep('confirmed');
    } catch (err) {
      toast.error(t('verification.errors.confirmFailed'));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">{t('verification.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6">
        <div className="max-w-lg bg-white border rounded-lg shadow p-6 text-center">
          <div className="text-3xl mb-3">🔒</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('verification.invalid.title')}</h2>
          <p className="text-gray-600">{t('verification.invalid.body')}</p>
          <p className="text-sm text-gray-500 mt-3">{error}</p>
        </div>
      </div>
    );
  }

  if (step === 'preview' && previewProperty) {
    return (
      <div className="min-h-screen bg-gray-100">
        <div className="bg-white border-b shadow-sm">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900">{t('verification.preview.title')}</h1>
              <p className="text-sm text-gray-500">{t('verification.preview.subtitle')}</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setStep('edit')}
                className="px-4 py-2 rounded border text-gray-600 hover:bg-gray-50"
              >
                {t('verification.preview.edit')}
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting}
                className="px-4 py-2 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {submitting ? t('common.loading') : t('verification.preview.confirm')}
              </button>
            </div>
          </div>
        </div>
        <PropertyDetailPage
          previewProperty={previewProperty}
          previewOnly
          previewBackTo={`/verify/${token}`}
        />
      </div>
    );
  }

  if (step === 'confirmed') {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center px-6">
        <div className="max-w-lg bg-white border rounded-lg shadow p-6 text-center">
          <div className="text-3xl mb-3">✅</div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">{t('verification.confirmed.title')}</h2>
          <p className="text-gray-600">{t('verification.confirmed.body')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      {showLanguageModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 px-4">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h2 className="text-xl font-semibold text-gray-900">{t('language.select')}</h2>
            <p className="mt-2 text-sm text-gray-600">{t('verification.languagePrompt')}</p>
            <div className="mt-4 grid gap-2 sm:grid-cols-2">
              {[
                { code: 'en', label: t('language.english') },
                { code: 'es', label: t('language.spanish') },
                { code: 'de', label: t('language.german') },
                { code: 'ru', label: t('language.russian') },
                { code: 'zh', label: t('language.chinese') },
                { code: 'ar', label: t('language.arabic') },
                { code: 'fr', label: t('language.french') },
              ].map((option) => (
                <button
                  key={option.code}
                  type="button"
                  onClick={() => {
                    i18n.changeLanguage(option.code);
                    setShowLanguageModal(false);
                  }}
                  className="w-full rounded border px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-2xl font-semibold text-gray-900">{t('verification.title')}</h1>
          <p className="text-sm text-gray-600 mt-2">{t('verification.subtitle')}</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="bg-white rounded-lg shadow p-6 space-y-6">
          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.basics')}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.reference')}</label>
                <div className="w-full px-3 py-2 border rounded bg-gray-50 text-gray-700 text-sm">
                  {formData.client_reference || '—'}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.propertyCategory')}</label>
                <select
                  value={formData.property_category}
                  onChange={(e) => setFormData((prev) => ({ ...prev, property_category: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="land_lot">{t('reportPage.form.propertyTypes.landLot')}</option>
                  <option value="apartment">{t('reportPage.form.propertyTypes.apartment')}</option>
                  <option value="house">{t('reportPage.form.propertyTypes.house')}</option>
                  <option value="commercial">{t('verification.fields.propertyTypes.commercial')}</option>
                  <option value="other">{t('verification.fields.propertyTypes.other')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.saleType')}</label>
                <select
                  value={formData.sale_type_detail}
                  onChange={(e) => setFormData((prev) => ({
                    ...prev,
                    sale_type_detail: e.target.value,
                    property_type: e.target.value === 'for_rent' ? 'rent' : 'sale',
                  }))}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="for_sale">{t('reportPage.form.saleTypes.forSale')}</option>
                  <option value="for_sale_by_owner">{t('reportPage.form.saleTypes.forSaleByOwner')}</option>
                  <option value="for_rent">{t('reportPage.form.saleTypes.forRent')}</option>
                  <option value="for_lease">{t('verification.fields.saleTypes.forLease')}</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.isOwner')}</label>
                <select
                  value={formData.is_owner}
                  onChange={(e) => setFormData((prev) => ({ ...prev, is_owner: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                >
                  <option value="">{t('common.select')}</option>
                  <option value="yes">{t('common.yes')}</option>
                  <option value="no">{t('common.no')}</option>
                </select>
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.description')}</label>
                <textarea
                  rows={4}
                  value={formData.description}
                  onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.contact')}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.contactName')}</label>
                <input
                  type="text"
                  value={formData.contact_name}
                  onChange={(e) => setFormData((prev) => ({ ...prev, contact_name: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.phone')}</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData((prev) => ({ ...prev, phone: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.whatsapp')}</label>
                <input
                  type="tel"
                  value={formData.whatsapp}
                  onChange={(e) => setFormData((prev) => ({ ...prev, whatsapp: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.email')}</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData((prev) => ({ ...prev, email: e.target.value }))}
                  className="w-full px-3 py-2 border rounded"
                />
              </div>
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.location')}</h2>
            <div className="text-sm text-gray-600">
              {t('verification.location.dragPin')}
            </div>
            <div className="rounded-lg border overflow-hidden">
              <MapComponent
                height="360px"
                markers={[{
                  id: 'verification-location',
                  lat: Number(formData.latitude) || 8.975585,
                  lng: Number(formData.longitude) || -79.520459,
                  title: t('verification.location.pinTitle'),
                }]}
                center={[
                  Number(formData.latitude) || 8.975585,
                  Number(formData.longitude) || -79.520459,
                ]}
                zoom={13}
                draggableMarkerId="verification-location"
                onMarkerDrag={(marker) => {
                  setFormData((prev) => ({
                    ...prev,
                    latitude: marker.lat,
                    longitude: marker.lng,
                  }));
                }}
                showControls={false}
                allowClick={false}
              />
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.propertyDetails')}</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.lotSize')}</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="any"
                    value={formData.lot_size_value}
                    onChange={(e) => setFormData((prev) => ({ ...prev, lot_size_value: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <select
                    value={formData.lot_size_unit}
                    onChange={(e) => setFormData((prev) => ({ ...prev, lot_size_unit: e.target.value }))}
                    className="px-2 py-2 border rounded bg-gray-50"
                  >
                    <option value="sq_m">m²</option>
                    <option value="sq_ft">ft²</option>
                    <option value="hectares">{t('verification.units.hectares')}</option>
                    <option value="acres">{t('verification.units.acres')}</option>
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.price')}</label>
                <div className="flex gap-2">
                  <input
                    type="number"
                    step="any"
                    value={formData.price}
                    onChange={(e) => setFormData((prev) => ({ ...prev, price: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  />
                  <select
                    value={formData.currency}
                    onChange={(e) => setFormData((prev) => ({ ...prev, currency: e.target.value }))}
                    className="px-2 py-2 border rounded bg-gray-50"
                  >
                    <option value="USD">USD</option>
                    <option value="EUR">EUR</option>
                    <option value="PAB">PAB</option>
                  </select>
                </div>
              </div>
              {['apartment', 'house'].includes(formData.property_category) && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.livingArea')}</label>
                    <div className="flex gap-2">
                      <input
                        type="number"
                        step="any"
                        value={formData.living_area_value}
                        onChange={(e) => setFormData((prev) => ({ ...prev, living_area_value: e.target.value }))}
                        className="w-full px-3 py-2 border rounded"
                      />
                      <select
                        value={formData.living_area_unit}
                        onChange={(e) => setFormData((prev) => ({ ...prev, living_area_unit: e.target.value }))}
                        className="px-2 py-2 border rounded bg-gray-50"
                      >
                        <option value="sqm">m²</option>
                        <option value="sq_ft">ft²</option>
                      </select>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.bedrooms')}</label>
                    <input
                      type="number"
                      min="0"
                      value={formData.bedrooms}
                      onChange={(e) => setFormData((prev) => ({ ...prev, bedrooms: e.target.value }))}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.bathrooms')}</label>
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={formData.bathrooms}
                      onChange={(e) => setFormData((prev) => ({ ...prev, bathrooms: e.target.value }))}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.yearBuilt')}</label>
                    <input
                      type="number"
                      min="1800"
                      value={formData.year_built}
                      onChange={(e) => setFormData((prev) => ({ ...prev, year_built: e.target.value }))}
                      className="w-full px-3 py-2 border rounded"
                    />
                  </div>
                </>
              )}
            </div>
          </section>

          {formData.property_category === 'land_lot' && (
            <section className="space-y-4">
              <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.infrastructure')}</h2>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.roadAccess')}</label>
                  <select
                    value={formData.road_access}
                    onChange={(e) => setFormData((prev) => ({ ...prev, road_access: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.roadSurface')}</label>
                  <select
                    value={formData.road_surface}
                    onChange={(e) => setFormData((prev) => ({ ...prev, road_surface: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="asphalt">{t('reportPage.form.roadSurfaceAsphalt')}</option>
                    <option value="gravel">{t('reportPage.form.roadSurfaceGravel')}</option>
                    <option value="dirt">{t('reportPage.form.roadSurfaceDirt')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.inCommunity')}</label>
                  <select
                    value={formData.in_community}
                    onChange={(e) => setFormData((prev) => ({ ...prev, in_community: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.waterConnection')}</label>
                  <select
                    value={formData.water_connection}
                    onChange={(e) => setFormData((prev) => ({ ...prev, water_connection: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.electricity')}</label>
                  <select
                    value={formData.electricity}
                    onChange={(e) => setFormData((prev) => ({ ...prev, electricity: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.fields.hasTitle')}</label>
                  <select
                    value={formData.has_title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, has_title: e.target.value }))}
                    className="w-full px-3 py-2 border rounded"
                  >
                    <option value="">{t('common.select')}</option>
                    <option value="yes">{t('common.yes')}</option>
                    <option value="no">{t('common.no')}</option>
                    <option value="not_sure">{t('reportPage.form.notSure')}</option>
                  </select>
                </div>
              </div>
            </section>
          )}

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.images')}</h2>
            <div className="text-sm text-gray-600">
              {t('verification.images.helper', { count: totalImageCount })}
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              {(property?.images || []).map((image) => {
                const src = normalizeMediaUrl(image.image_url) || storageUrl(image.file_path);
                const checked = keepImageIds.includes(image.id);
                return (
                  <label key={image.id} className="flex items-center gap-3 border rounded p-3">
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleExistingImageToggle(image.id)}
                    />
                    <img src={src} alt="" className="w-24 h-20 object-cover rounded" />
                    <span className="text-sm text-gray-600">{checked ? t('verification.images.keep') : t('verification.images.remove')}</span>
                  </label>
                );
              })}
            </div>

            {draftImages.length > 0 && (
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700">{t('verification.images.newUploads')}</div>
                <div className="grid gap-4 md:grid-cols-2">
                  {draftImages.map((image) => (
                    <div key={image.file_path} className="flex items-center gap-3 border rounded p-3">
                      <img src={normalizeMediaUrl(image.image_url)} alt="" className="w-24 h-20 object-cover rounded" />
                      <button
                        type="button"
                        onClick={() => handleDraftImageRemove(image.file_path)}
                        className="text-sm text-red-600"
                      >
                        {t('verification.images.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('verification.images.upload')}</label>
              <input type="file" accept=".jpg,.jpeg" multiple onChange={handleNewImagesChange} />
              {newImages.length > 0 && (
                <div className="mt-3 space-y-2">
                  {newImages.map((file, index) => (
                    <div key={`${file.name}-${index}`} className="flex items-center justify-between text-sm text-gray-600">
                      <span>{file.name}</span>
                      <button
                        type="button"
                        className="text-red-600"
                        onClick={() => handleRemoveNewImage(index)}
                      >
                        {t('verification.images.remove')}
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          <section className="space-y-4">
            <h2 className="text-lg font-semibold text-gray-900">{t('verification.sections.confirmation')}</h2>
            <label className="flex items-center gap-3 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={formData.mark_sold}
                onChange={(e) => setFormData((prev) => ({ ...prev, mark_sold: e.target.checked }))}
              />
              {t('verification.fields.markSold')}
            </label>
          </section>
        </div>

        <div className="flex justify-end">
          <button
            type="button"
            onClick={handlePreview}
            disabled={submitting}
            className="px-5 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
          >
            {submitting ? t('common.loading') : t('verification.actions.preview')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PropertyVerificationPage;
